import React, {useEffect} from 'react'
import {removeItem, showItemEditor} from '../../store/slices/shoppingCartSlice'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import * as types from '../../schema'
import clsx from 'clsx'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faBed,
  faCalendarAlt,
  faEdit,
  faFileInvoiceDollar,
  faImage,
  faInfoCircle,
  faTrash,
  faUser
} from '@fortawesome/free-solid-svg-icons'
import {generateUUID} from '../../services/utils'
import {formatDisplayDate, toPropertyDateTime} from '../../services/dateUtils'
import {addToastNotification} from '../../store/slices/toastNotificationSlice'
import {getLogger} from '../../services/logging'
import {
  selectPropertyById,
  selectUnitGroupsById
} from '../../store/slices/configSlice'
import ShoppingCartPricingSummary from './ShoppingCartPricingSummary'
import useWindowDimensions from '../../hooks/useWindowDimensions'
import CancellationPolicy from '../common/CancellationPolicy'
import NoShowPolicy from '../common/NoShowPolicy'
import {pushDataLayerEvent} from '../../services/dataLayer'
import MemberRateBadge from '../common/MemberRateBadge'
import SpecialRateBadge from '../common/SpecialRateBadge'

const logger = getLogger('ShoppingCartItem')

export interface ShoppingCartItemProps {
  item: types.ShoppingCartItem
  itemIndex?: number
  showItemIndex?: boolean
  isCart?: boolean
  className?: string
  validationError?: types.ShoppingCartValidationError | null
}

const ShoppingCartItem = ({
  item,
  itemIndex = -1,
  showItemIndex = false,
  isCart = false,
  className = '',
  validationError
}: ShoppingCartItemProps) => {
  const [showCancellationPolicy, setShowCancellationPolicy] = React.useState(
    false
  )
  const {isLarge} = useWindowDimensions()
  const dispatch = useAppDispatch()

  const unitGroupConfigLookup = useAppSelector(selectUnitGroupsById)
  const propertyConfig = useAppSelector(selectPropertyById(item.propertyId))
  const unitGroupConfig = unitGroupConfigLookup
    ? unitGroupConfigLookup[item?.offer?.unitGroup?.id]
    : null
  const unitGroupName = unitGroupConfig?.name || item?.offer?.unitGroup?.name
  const roomImage = unitGroupConfig?.images?.[0]
  const {promoCode, corporateCode} = item.offer
  const hasMemberRate = item.offer?.isMemberRate || false

  const handleEditItem = () => {
    logger.info({
      message: 'Editing room item in shopping cart...',
      data: {item}
    })
    pushDataLayerEvent({
      event: 'Shopping_Cart_Edit',
      category: 'IBE',
      value: 1
    })
    dispatch(showItemEditor({item, mode: 'Edit'}))
  }

  const handleRemoveItem = () => {
    logger.info({
      message: 'Removing room item from shopping cart...',
      data: {item}
    })
    pushDataLayerEvent({
      event: 'Shopping_Cart_Remove',
      category: 'IBE',
      value: 1
    })
    dispatch(removeItem(item.id))

    const roomName = item?.offer?.unitGroup?.name || 'Room'
    dispatch(
      addToastNotification({
        id: generateUUID(),
        type: 'remove',
        message: `${roomName} successfully removed from your shopping cart.`
      })
    )
  }

  // close cancellation policy dropdown on window clicks
  useEffect(() => {
    const handleClick = () => {
      if (showCancellationPolicy) {
        setShowCancellationPolicy(false)
      }
    }
    window.addEventListener('click', handleClick)
    return () => {
      window.removeEventListener('click', handleClick)
    }
  }, [showCancellationPolicy])

  return (
    <div className={clsx('w-full flex flex-col gap-2', className)}>
      {/* card header */}
      {(validationError || showItemIndex) && (
        <div className="flex flex-col gap-2 mb-2">
          {validationError && (
            <div
              className="w-full alert shadow-sm border-1 border-gray-200 mb-2"
              tabIndex={0}
            >
              <div className="text-error">{validationError.message}</div>
            </div>
          )}
          {showItemIndex && (
            <div className="badge bg-base-400 text-light-primary text-xs font-semibold">
              room {itemIndex + 1}
            </div>
          )}
        </div>
      )}

      {/* item image and property/unit details */}
      <div className="flex flex-col sm:flex-row gap-4 w-full">
        <div className="w-full sm:w-1/3">
          {/* item image */}
          <div className="w-full h-full">
            {roomImage ? (
              <img
                src={roomImage.image}
                alt={roomImage.altText}
                className="object-cover rounded-xl"
              />
            ) : (
              <div className="h-36 flex items-center justify-center bg-gray-200 rounded-xl">
                <FontAwesomeIcon
                  icon={faImage}
                  className="text-2xl"
                  title="Empty room image"
                />
              </div>
            )}
          </div>
        </div>

        {/* offer details */}
        <div className="w-full sm:w-2/3 flex flex-col gap-2">
          {/* promo code badge */}
          {promoCode && <SpecialRateBadge label={promoCode} />}

          {/* corporate code badge */}
          {!promoCode && corporateCode && (
            <SpecialRateBadge label={corporateCode} />
          )}

          {/* member rate */}
          {!promoCode && !corporateCode && hasMemberRate && <MemberRateBadge />}

          {/* property/room info */}
          <div className="w-full text-md">
            <div className="text-accent font-semibold uppercase">
              {propertyConfig?.location}
            </div>
            <div className="text-lg font-semibold">{propertyConfig?.name}</div>
            <div className="flex flex-row">
              <div className="w-6 flex items-center justify-center">
                <FontAwesomeIcon icon={faBed} className="text-accent mr-2" />
              </div>
              <div>{unitGroupName}</div>
            </div>
            <div className="flex flex-row">
              <div className="w-6 items-center justify-center">
                <FontAwesomeIcon
                  icon={faFileInvoiceDollar}
                  className="text-accent mr-2"
                />
              </div>
              <div>{item.offer.ratePlan.name}</div>
            </div>
            <div className="flex flex-row">
              <div className="w-6 items-center justify-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-accent mr-2"
                />
              </div>
              <div>
                {formatDisplayDate(toPropertyDateTime(item.offer.arrival))} -{' '}
                {formatDisplayDate(toPropertyDateTime(item.offer.departure))}
              </div>
            </div>
            <div className="flex flex-row">
              <div className="w-6 items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-accent mr-2" />
              </div>
              <div>
                {item.guests === 1 ? '1 guest' : `${item.guests} guests`}
              </div>
            </div>

            {/* cancellation / no show policy */}
            <div
              className={clsx('dropdown w-full', {
                'dropdown-open': showCancellationPolicy,
                'dropdown-hover': isLarge
              })}
            >
              <div className="flex flex-row">
                <div className="w-6 items-center justify-center">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="text-accent mr-1"
                    title="Cancellation policy info"
                  />
                </div>

                <a
                  className="link"
                  onClick={() => {
                    if (!isLarge) {
                      setShowCancellationPolicy(!showCancellationPolicy)
                    }
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                    if (e.key === 'Enter') {
                      setShowCancellationPolicy(!showCancellationPolicy)
                    }
                  }}
                  onBlur={() => {
                    setShowCancellationPolicy(false)
                  }}
                  tabIndex={0}
                  aria-label="Show cancellation/no show policies."
                >
                  Cancellation Policy
                </a>
              </div>

              <div
                tabIndex={0}
                className="card compact dropdown-content w-full bg-white border rounded-box z-[1] w-64 shadow-lg"
              >
                <div tabIndex={0} className="card-body">
                  <div className="flex flex-col gap-4">
                    <CancellationPolicy offer={item.offer} />
                    <NoShowPolicy offer={item.offer} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t my-2"></div>

      {/* item price details */}
      <ShoppingCartPricingSummary
        offer={item.offer}
        additionalServices={item.additionalServices}
        isCart={isCart}
        className="w-full"
      />

      {/* item actions - edit/remove */}
      {isCart && (
        <div className="flex-0 flex flex-row justify-center gap-3 my-2">
          <a
            className={clsx(
              'flex flex-row gap-1 items-center',
              'link text-xs md:text-sm font-medium text-accent no-underline hover:underline'
            )}
            onClick={handleRemoveItem}
            onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
              if (e.key === 'Enter') {
                handleRemoveItem()
              }
            }}
            tabIndex={0}
            aria-label="Remove this room from your shopping cart."
          >
            <FontAwesomeIcon
              icon={faTrash}
              className="text-accent"
              title="Delete shopping cart item"
            />
            <span className="text-light-primary">remove room</span>
          </a>
          <a
            className={clsx(
              'flex flex-row gap-1 items-center',
              'link text-xs md:text-sm font-medium no-underline hover:underline'
            )}
            onClick={handleEditItem}
            onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
              if (e.key === 'Enter') {
                handleEditItem()
              }
            }}
            tabIndex={0}
            aria-label="Edit this room's details."
          >
            <FontAwesomeIcon
              icon={faEdit}
              className="text-accent"
              title="Edit shopping cart item"
            />
            <span className="text-light-primary">edit room</span>
          </a>
        </div>
      )}
    </div>
  )
}

export default ShoppingCartItem
